import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { of, forkJoin } from "rxjs";
import { filter, map, switchMap, take, catchError } from "rxjs/operators";
import { of as observableOf, BehaviorSubject, Subject, throwError } from "rxjs";
import { environment } from "../environments/environment";
import { StorageService, StorageKeys } from "./storage.service";
import { WindowService } from "./window.service";
import { UserService } from "./user.service";
import { LogService } from "./log.service";
import moment from "moment";
import { HelperService } from "./helper.service";
import { Md5 } from "ts-md5/dist/md5";
import { TranslateService } from "@ngx-translate/core";
import { GAService } from "./ga.service";
import { ProductCategory, ProductListItem, FenderGearListItem } from "../models";
import { ProductRequirement } from "../models/community.model";
import { format, parse } from "date-fns";

@Injectable({
  providedIn: "root",
})
export class ProductService {
  private _savedProducts: any;
  private _dirty: boolean = false;
  private _products: any;

  private _news: any;

  public hasGottenProducts: boolean = false;

  public news$: BehaviorSubject<any> = new BehaviorSubject(false);
  public products$: BehaviorSubject<any> = new BehaviorSubject(false);
  public redeemS17ElligibilityRetrieved$: BehaviorSubject<any> = new BehaviorSubject(false);
  public productDataError$: Subject<boolean> = new Subject();
  public productRegistered$: EventEmitter<number> = new EventEmitter();
  public studioOneStatus$: BehaviorSubject<any> = new BehaviorSubject(null);

  public gettingProducts: boolean = false;

  public productDetailsMap: any = {};
  public productMap: {
    [key: number]: ProductListItem;
  } = {};
  public productsByAcquired: Array<any> = [];
  public productsByReleaseDate: Array<any> = [];
  public productErpIDs: Array<string> = [];
  public productsByErpIDs: any = {};
  public productsByStringIDs: any = {};
  public productsByProductID: any = {};
  public prodImagesMap: any = {};
  public subscriptionProductsMap: any = {};
  public productGroups: any = {};
  public learnProducts: any = [];
  public hasElligibleS1ForExchange: boolean = false;
  public sphereElligibleUpgradeProducts: Array<any> = [];
  public redeemableProducts: Array<any>;

  public redeemableBundledProducts: Array<any> = [];

  public documentLanguageIds: any = {
    English: "en",
    German: "de",
    French: "fr",
    Spanish: "es",
    Japanese: "ja",
    Chinese: "zh",
    Turkish: "tr",
    Français: "fr",
    Deutsch: "de",
    Español: "es",
    日本語: "ja",
    한글: "ko",
  };

  public s16Released: boolean = moment() >= moment("2022-09-29 08:00:00-05:00");

  public hasElligibleS15HardwareUpgrade = false;
  public elligibleS15HardwareUpgrades = {
    atom: true,
    "atom.producers.lab": true,
    AudioBoxiOne: true,
    "AudioBox.iOne.Platinum": true,
    AudioBoxiTwo: true,
    AudioBoxiTwoStudio: true,
    "abox96k.ultimate": true,
    "audiobox.usb.96": true,
    "audiobox.usb.96.blk": true,
    "audiobox.96.studio": true,
    "audiobox.usb.96.blk.studio": true,
    faderportv2: true,
    faderport16: true,
    faderport8: true,
    iostation24c: true,
    quantum: true,
    quantum2: true,
    quantum2626: true,
    studio1810c: true,
    studio1824c: true,
    "Studio.192": true,
    studio24c: true,
    studio26c: true,
    studio68c: true,
    "studiolive.16.0.2.usb": true,
    "studiolive.16.r": true,
    "studiolive.24.r": true,
    "studiolive.32.r": true,
    "studiolive.32s.iii": true,
    "studiolive.32sc.iii": true,
    "studiolive.32sx.iii": true,
    "studiolive.64.iii": true,
    "StudioLive.AR12.USBC": true,
    "StudioLive.AR16.USBC": true,
    "StudioLive.AR8.USBC": true,
  };

  public ecosystemProducts: any = {
    "earmix.16m": true,
    cable5: true,
    "studiolive.16.iii": true,
    "studiolive.16.r": true,
    "studiolive.24.iii": true,
    "studiolive.24.r": true,
    "studiolive.32.iii": true,
    "studiolive.32.r": true,
    "studiolive.32s.iii": true,
    "studiolive.32sc.iii": true,
    "studiolive.32sx.iii": true,
    "studiolive.64.iii": true,
    "nsb.16.8": true,
    "nsb.8.8": true,
  };

  public erpIDTitleMap: any = {
    STUDIOONE3: "Studio One 3",
    STUDIOONE4: "Studio One 4",
    STUDIOONE5: "Studio One 5",
  };

  public categoryMap: any = {};

  public lastProductListScrollPosition: number;
  public hardRefreshProducts: boolean = false;
  public newProductRegistered$: BehaviorSubject<any> = new BehaviorSubject<boolean>(false);
  public newProductRegistered = this.newProductRegistered$.asObservable();
  public s1SixToSevenElligiblePurchaseDate: Date = new Date("2024-08-01");

  public simpleProductList: {
    products: Array<ProductListItem>;
    categories: any;
  };

  public studioOneStatus: any;
  public latestMajorStudioOneStringId: string = environment.latestMajorStudioOneStringId;
  public hasRecordingProducts: boolean = false;
  public hasInstrumentProducts: boolean = false;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private _win: WindowService,
    private _translate: TranslateService,
    private _log: LogService,
    private _helpers: HelperService,
    private userService: UserService,
    private gaService: GAService
  ) {
    this._helpers.userID = this.userService.user ? this.userService.user.id : undefined;
  }

  public get(method) {
    return this.http.get(environment.paeApiUrl + `product/${method}`, this._helpers.getHttpOptions());
  }

  public post(method, args) {
    return this.http.post(environment.paeApiUrl + `product/${method}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  public put(method, args) {
    return this.http.put(environment.paeApiUrl + `product/${method}`, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  get products() {
    if (!this._products) this._products = this.storage.getItem(StorageKeys.PRODUCTS);
    return this._products;
  }
  set products(prods) {
    if (prods) {
      this._products = prods;
      this.storage.setItem(StorageKeys.PRODUCTS, prods);
    } else {
      this._products = false;
      this.storage.removeItem(StorageKeys.PRODUCTS);
    }
  }

  private _productImagesMap: any;
  get productImagesMap() {
    if (!this._productImagesMap) this._productImagesMap = this.storage.getItem(StorageKeys.PRODUCTIMAGESMAP);
    return this._productImagesMap;
  }

  set productImagesMap(map) {
    this._productImagesMap = map;
    this.storage.setItem(StorageKeys.PRODUCTIMAGESMAP, map);
  }

  public initProducts(getProductsIfNotGotten, mobile?) {
    this._savedProducts = this.products;
    let user = this.userService.user;
    if (user) {
      if (this._savedProducts) {
        let prodIDs = [];
        if (this._savedProducts.hardware && this._savedProducts.hardware.length) {
          this._savedProducts.hardware.forEach((item) => prodIDs.push(item.id));
        }
        if (this._savedProducts.software && this._savedProducts.software.length) {
          this._savedProducts.software.forEach((item) => prodIDs.push(item.id));
        }
        let hashedProdIDs = Md5.hashStr(JSON.stringify(prodIDs));
        if (!user.cached_product_ids || hashedProdIDs != user.cached_product_ids) {
          // get new product data because their product data was invalidated or it changed.
          this._win.setTimeout(() => {
            this.getProducts(true, false, false, mobile).subscribe();
          }, 2000);
        } else if (!this.storage.checkTimestamp("product_image")) {
          this._win.setTimeout(() => {
            this.getProducts(false, true, false, mobile).subscribe();
          }, 2000);
        } else {
          let productData;
          if (mobile) {
            productData = this._handleMobileProductData(this._savedProducts);
          } else {
            productData = this._handleProductData(this._savedProducts);
          }
          if (productData) {
            this.hasGottenProducts = true;
            this.products$.next(productData);
          }
        }
      } else if (getProductsIfNotGotten) {
        this.getProducts(false, false, false, mobile).subscribe();
      }
    }
  }

  public listProductDataMap: any = {};
  public getSimpleProductList() {
    this.isGettingProducts = true;
    return this.http.get(environment.paeApiUrl + "product/list/all", this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        if (data.products?.length) {
          data.products.forEach((item) => {
            this.listProductDataMap[item.id] = item;
          });
        }

        if (data.categories) this.processProductCategories(data.categories);
        this.isGettingProducts = false;
        return data;
      })
    );
  }

  public getDetailForAssetIds(id, assetIds) {
    let assetIdsString = assetIds.join(",");
    return this.http.get(environment.paeApiUrl + `product/detail/${id}?assetIds=${assetIdsString}`, this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public regenerateSnapshot(id) {
    return this.http.get(environment.paeApiUrl + `product/detail/${id}?clearSnapshot=true`, this._helpers.getHttpOptions());
  }

  /**
   * Does the current user own a product that satisfies this `ProductRequirement`? Called
   * by the community participation gate (`computeParticipationGate`) when a community
   * uses `participation_condition === "product_owner"`.
   *
   * Items are flat `ProductListItem`s (no `.product` sub-object) — see the paeapi
   * `transformProductListData` output shape. Match logic:
   *
   *   - `string_id` → `productsByStringIDs[value]` (fast-path map, exact match)
   *   - `sku`       → scan `productsByAcquired` for `item.sku === value`. The paeapi
   *                   transformer surfaces `Product.fmic_material_number` as `sku`,
   *                   which is what the admin picker saves from FMIC search results.
   *                   `navId` on the item carries `Product.erpId` — a different NAV
   *                   identifier — so we intentionally do NOT fall back to it.
   *   - `productNo` → productNo is NOT surfaced on list-shape items (the license server's
   *                   assets-table data doesn't carry it, and pulling snapshots for every
   *                   Fender product a user owns is too heavy). Fall back to comparing
   *                   the requirement's stored `label` (the FMIC title the admin picked)
   *                   against `item.title` case-insensitively. Less strict than SKU but
   *                   avoids the round-trip.
   *
   * Callers MUST wait until `hasGottenProducts === true` before trusting a `false`
   * response — an empty product cache always reports "doesn't own". Product-gated
   * communities defer their gate evaluation via `products$` for this reason.
   */
  public ownsProductRequirement(req: ProductRequirement): boolean {
    if (!req || !req.value) return false;
    switch (req.type) {
      case "string_id":
        return !!this.productsByStringIDs[req.value];
      case "sku":
        return this.productsByAcquired.some((item) => item?.sku === req.value);
      case "productNo": {
        const target = (req.label || "").trim().toLowerCase();
        if (!target) return false;
        return this.productsByAcquired.some((item) => (item?.title || "").trim().toLowerCase() === target);
      }
    }
    return false;
  }

  public isGettingProducts: boolean = false;
  public getProducts(forceRefresh?, getNewImages?, clearCache = true, mobileOnly = false) {
    return this.getSimpleProductList().pipe(
      map((result: { products: Array<ProductListItem>; categories: any }) => {
        this.simpleProductList = result;
        let fenderProducts = this.simpleProductList.products.filter((product) => product.id > 10000);
        let presonusProducts = this.simpleProductList.products.filter((product) => product.id < 10000);
        this.hasRecordingProducts = presonusProducts.length > 0;
        this.hasInstrumentProducts = fenderProducts.length > 0;
        this.prodArraySorter(this.simpleProductList.products);
        this.hasGottenProducts = true;
        this._handleProductData(this.simpleProductList.products);
        this.storage.setTimestamp("product_image", 24);
        this.checkStudioOneSevenRedeemElligibility();
        this.products$.next(this.simpleProductList);
        this.userService.subDetails$
          .pipe(
            filter((item) => !!item),
            take(1)
          )
          .subscribe((result) => {
            this.determineStudioOneStatus(this.simpleProductList);
          });
        return this.simpleProductList;
      })
    );
  }

  public upgradableS17Asset: any;
  public determineStudioOneStatus(products) {
    let subscriptionStatus = this.userService.subDetails?.subscription?.status;
    let studioOneProducts = products.products.filter((product) => product.stringId?.indexOf("studioapp") > -1);
    let hasFenderStudioPro = studioOneProducts.some((product) => product.title?.indexOf("Fender") > -1);
    if (studioOneProducts.length) {
      if (this.productsByStringIDs[this.latestMajorStudioOneStringId]) {
        this.getDetailForAssetIds(this.productsByStringIDs[this.latestMajorStudioOneStringId].id, this.productsByStringIDs[this.latestMajorStudioOneStringId].assetIds).subscribe((result) => {
          if (result.length) {
            let license = result[0];
            if (license.bundledProducts) this.redeemableBundledProducts = license.bundledProducts.filter((item) => item.redeemed === false);

            // Append bundled products to simpleProductList
            if (this.redeemableBundledProducts?.length && this.simpleProductList?.products) {
              // first make sure the redeemableBundledProducts are not already in the simpleProductList
              this.redeemableBundledProducts = this.redeemableBundledProducts.filter((item) => !this.simpleProductList.products.some((product) => product.id === item.id));
              if (this.redeemableBundledProducts?.length) {
                this.simpleProductList.products = [...this.redeemableBundledProducts, ...this.simpleProductList.products];
                // Emit updated product list
                this.products$.next(this.simpleProductList);
              }
            }

            if (license.extendedSupportDate && license.extendedSupportDate != "NO_EXTENDED_SUPPORT") {
              this.studioOneStatus = {
                status: "registered_perpetual",
                color: "green",
                message: this._translate.instant("s1_status.eligible_for_updates_until", { extendedSupportDate: format(parse(license.extendedSupportDate, "yyyy-MM-dd", new Date()), "MMM do, yyyy") }),
              };
              this.studioOneStatus$.next(this.studioOneStatus);
            } else if ((!license.extendedSupportDate || license.extendedSupportDate == "NO_EXTENDED_SUPPORT") && license.fallbackVersion) {
              this.studioOneStatus = {
                status: "no_extended_support",
                color: "orange",
                message: this._translate.instant("s1_status.update_eligibility_expired", { fallbackVersion: license.fallbackVersion }),
              };
              let productPointVersion = this.productsByStringIDs[this.latestMajorStudioOneStringId].version.split(".")[1];
              let fallbackPointVersion = license.fallbackVersion.split(".")[1];
              if (parseInt(fallbackPointVersion) < parseInt(productPointVersion)) {
                this.studioOneStatus = {
                  status: "no_extended_support",
                  color: "red",
                  message: this._translate.instant("s1_status.eligibility_expired_access_latest", {
                    fallbackVersion: license.fallbackVersion,
                    version: this.productsByStringIDs[this.latestMajorStudioOneStringId].version,
                  }),
                };
              }
              this.studioOneStatus$.next(this.studioOneStatus);
            }
          }
          if (subscriptionStatus == "active") {
            this.studioOneStatus = {
              status: "registered_subscription",
              color: "green",
              message: this._translate.instant("s1_status.you_have_unlimited"),
            };
            this.studioOneStatus$.next(this.studioOneStatus);
          }
        });
      } else {
        if (subscriptionStatus == "active") {
          this.studioOneStatus = {
            status: "registered_subscription",
            color: "green",
            message: this._translate.instant("s1_status.you_have_unlimited"),
          };
          this.studioOneStatus$.next(this.studioOneStatus);
        } else {
          if (this.productsByStringIDs[this.latestMajorStudioOneStringId] != "studioapp7.pro" && this.productsByStringIDs["studioapp7.pro"]) {
            this.getDetailForAssetIds(this.productsByStringIDs["studioapp7.pro"].id, this.productsByStringIDs["studioapp7.pro"].assetIds).subscribe((result) => {
              if (result.length) {
                let license = result[0];
                this.upgradableS17Asset = license;
                if (license.extendedSupportDate) {
                  // Check if the extended support date is in the future (not in the past)
                  let supportDate: any;
                  let registrationDate: any;
                  if (license.extendedSupportDate != "NO_EXTENDED_SUPPORT") {
                    supportDate = moment(license.extendedSupportDate);
                  } else {
                    registrationDate = moment(license.aquired);
                  }
                  let canRedeem8 = false;
                  if (supportDate && supportDate.isValid() && supportDate.isAfter(moment())) canRedeem8 = true;
                  if (registrationDate && registrationDate.isValid() && registrationDate.isAfter(moment("2026-01-12"))) canRedeem8 = true;
                  if (canRedeem8) {
                    this.studioOneStatus = {
                      status: "can_redeem_fsp8",
                      color: "green",
                      message: this._translate.instant("s1_status.can_redeem_fsp8"),
                    };
                    this.studioOneStatus$.next(this.studioOneStatus);
                  }
                }
              } else {
                const translationString = hasFenderStudioPro ? "s1_status.registered_old_version" : "s1_status.registered_old_version_presonus";
                this.studioOneStatus = {
                  status: "registered_old_version",
                  color: "orange",
                  message: this._translate.instant(translationString),
                };
                this.studioOneStatus$.next(this.studioOneStatus);
              }
            });
          } else {
            const translationString = hasFenderStudioPro ? "s1_status.registered_old_version" : "s1_status.registered_old_version_presonus";
            this.studioOneStatus = {
              status: "registered_old_version",
              color: "orange",
              message: this._translate.instant(translationString),
            };
            this.studioOneStatus$.next(this.studioOneStatus);
          }
        }
      }
    } else {
      if (subscriptionStatus == "active") {
        this.studioOneStatus = {
          status: "registered_subscription",
          color: "green",
          message: this._translate.instant("s1_status.you_have_unlimited"),
        };
        this.studioOneStatus$.next(this.studioOneStatus);
      } else {
        this.studioOneStatus = {
          status: "not_registered",
          message: this._translate.instant("s1_status.not_registered"),
        };
        this.studioOneStatus$.next(this.studioOneStatus);
      }
    }
  }

  public productCategories: Array<ProductCategory>;
  public getProductCategories() {
    return this.http.get(environment.paeApiUrl + "product/categories", this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        this.processProductCategories(data);
        return data;
      })
    );
  }

  public productCategoryMap: any = {};
  processProductCategories(data) {
    data.forEach((item) => {
      this.productCategoryMap[item.string_id] = item;
      if (item.children) {
        this.processProductCategories(item.children);
      }
    });
    data.sort((a, b) => a.sort_order - b.sort_order);
    data.forEach((category) => {
      if (category.children && category.children.length > 0) {
        category.children.sort((a, b) => a.sort_order - b.sort_order);
      }
    });
    this.productCategories = data;
  }

  get news() {
    if (!this._news) this._news = this.storage.getItem(StorageKeys.NEWS_UPDATES);
    return this._news;
  }

  set news(news) {
    this._news = news;
    this.storage.setItem(StorageKeys.NEWS_UPDATES, news);
  }

  public getHomeRss(limit?, app = "mypresonus.web") {
    let language = this._translate.getDefaultLang();
    if (language != "de" && language != "en") {
      language = "en";
    }
    const storedUpdates = this.news;
    if (storedUpdates) {
      this.news$.next(limit ? storedUpdates.slice(0, limit) : storedUpdates);
    }
    return this.http.get(environment.paeApiUrl + "product/rss_feed?language=" + language + "&version=" + app, this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        let rssContent: any = [];
        let entries = data.feed.entry;
        if (!Array.isArray(entries)) entries = [entries];
        for (var i = 0; entries.length > i; i++) {
          let displayObj = {
            id: entries[i].id,
            title: entries[i].title,
            //"display_date": moment(entries[i].updated).format('MMM Do, YYYY'),
            display_date: entries[i].updated,
            summary: entries[i].summary,
          };
          if (entries[i].link && Array.isArray(entries[i].link) && entries[i].link.length) {
            for (let x = 0; entries[i].link.length > x; x++) {
              let link = entries[i].link[x];
              if (link["$"].type && link["$"].type.indexOf("image") != -1 && link["$"]["device-pixel-ratio"] == "2x") {
                displayObj["image"] = encodeURI(link["$"].href);
              }
              if (link["$"].href.indexOf("http") != -1 && link["$"].rel != "enclosure") displayObj["link"] = link["$"].href;
            }
          } else {
            if (entries[i].link["$"] && entries[i].link["$"].href && entries[i].link["$"].href.indexOf("http") != -1 && entries[i].link["$"].rel != "enclosure") {
              displayObj["link"] = entries[i].link["$"].href;
            }
          }
          if (i >= 10) break;
          rssContent.push(displayObj);
        }
        this.news = rssContent;
        this.news$.next(limit ? rssContent.slice(0, limit) : rssContent);
        return rssContent;
      })
    );
  }

  public getProduct(id) {
    return this.productMap[id] ? this.productMap[id] : this.productsByProductID[id];
  }

  public getProductSnapshot(productId: string, version?: string, betaProgramId?: any, missingFenderSerial?: string): any {
    let url = environment.paeApiUrl + "product/detail/" + productId;
    if (version || betaProgramId) {
      url += "?";
      if (version) url += "version=" + version;
      if (betaProgramId) url += (version ? "&" : "") + "betaProgramId=" + betaProgramId;
    } else if (missingFenderSerial) {
      url += "?missingFenderSerial=" + missingFenderSerial;
    }
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  /**
   * Fetches the product snapshot with localized PIM copy overlaid for the given locale (read-only;
   * never persists a snapshot). Used by the PDP when a product has no admin-authored localized
   * description but has PIM data and the user's language is not English.
   */
  public getLocalizedProductSnapshot(productId: string | number, lang: string): any {
    return this.http.get(environment.paeApiUrl + `product/detail/${productId}/localized?lang=${encodeURIComponent(lang)}`, this._helpers.getHttpOptions());
  }

  public getHardwareDownloads(id) {
    return this.http.get(environment.paeApiUrl + `product/downloads/${id}`, this._helpers.getHttpOptions());
  }

  public getLicenseDetails(licenseId: string): any {
    return this.http.get(environment.paeApiUrl + "product/license/detail/" + licenseId, this._helpers.getHttpOptions());
  }

  search(query) {
    return this.http.get(environment.apiUrl + "products/search/" + encodeURI(query), this._helpers.getHttpOptions()).pipe();
  }

  searchv2(searchField, query, limit = 5, category?: string) {
    let url = environment.paeApiUrl + `product/search?searchField=${searchField}&query=${query}&limit=${limit}`;
    if (category) url += `&category=${category}`;
    return this.http.get(url, this._helpers.getHttpOptions()).pipe();
  }

  employeeRedeemSphere(coupon) {
    return this.http.get(environment.paeApiUrl + "checkout/redeem-employee-subscription/" + coupon, this._helpers.getHttpOptions()).pipe();
  }

  getSubscriptionProductDetails(asset_id) {
    return this.http.get(environment.apiUrl + "products/software/" + asset_id, this._helpers.getHttpOptions());
  }

  public getTokenizedUrl(folder, filename, siteId, productName, productId, productFileName, productFileSize, productVersionNumber) {
    return this.http
      .post(
        environment.paeApiUrl + "product/refresh-tokenized-url/",
        JSON.stringify({
          folder: folder,
          filename: filename,
          siteId: siteId,
          productName: productName,
          productId: productId,
          productFileName: productFileName,
          productFileSize: productFileSize,
          productVersionNumber: productVersionNumber,
        }),
        this._helpers.getHttpOptions()
      )
      .pipe();
  }

  public getOemKey(args) {
    return this.http.post(environment.paeApiUrl + "product/oemkey/", JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        this._dirty = true;
        return data;
      })
    );
  }

  public getLearnProduct(id) {
    return this.learnProducts.filter((item) => item.erpID == id)[0];
  }

  public redeemProduct(opts: {
    id?: any;
    configKey?: any;
    groupId?: any;
    productsToUpgrade?: any;
    options?: any;
    variant?: any;
    timeOutDays?: any;
    giveAway?: any;
    oldProductKey?: any;
    userId?: any;
    markPendingFulfilled?: boolean;
  }) {
    let args: any = {};
    if (opts.id && Array.isArray(opts.id)) {
      args.ids = opts.id;
    } else if (opts.id) {
      args.id = opts.id;
    }
    if (opts.configKey) args.configKey = opts.configKey;
    if (opts.groupId) args.groupId = opts.groupId;
    if (opts.productsToUpgrade) args.productsToUpgrade = opts.productsToUpgrade;
    if (opts.options) args.options = opts.options;
    if (opts.variant) args.variant = opts.variant;
    if (opts.timeOutDays) args.timeOutDays = opts.timeOutDays;
    if (opts.giveAway) args.giveAway = opts.giveAway;
    if (opts.oldProductKey) args.oldProductKey = opts.oldProductKey;
    if (opts.userId) args.userId = opts.userId;
    if (opts.markPendingFulfilled) args.markPendingFulfilled = opts.markPendingFulfilled;
    return this.http.post(environment.paeApiUrl + "product/redeem", JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public redeemBetaProduct(betaId) {
    return this.http.put(environment.paeApiUrl + "betas/redeem/" + betaId, JSON.stringify({}), this._helpers.getHttpOptions()).pipe();
  }

  public performTransfer(args) {
    return this.http.post(environment.paeApiUrl + "checkout/transfer/", JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public performUpgrade(args) {
    let url = environment.paeApiUrl + "product/upgrade";
    return this.http.post(url, JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public deactivateSoftware(args) {
    let url = "product/deactivate-software/";
    if (args.hardwareSerial) url = "product/deactivate-mixer-addon";

    return this.http.post(environment.paeApiUrl + url, JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public activateSoftware(args) {
    return this.http.post(environment.paeApiUrl + "product/activate-software/", JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public saveMissingFenderSerial(args) {
    return this.http.post(environment.paeApiUrl + "product/register-missing-serial", JSON.stringify(args), this._helpers.getHttpOptions()).pipe();
  }

  public deleteMissingFenderSerial(serial) {
    return this.http.delete(environment.paeApiUrl + "product/missing-serials/" + serial, this._helpers.getHttpOptions()).pipe();
  }

  public getFirmwareInstructions(product_id: number, version: string, dealer: boolean = false) {
    let url = `${environment.paeApiUrl}product/firmware-instructions/${product_id}?firmwareVersion=${version}`;
    if (dealer) url += "&isDealerProduct=true";
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  public getFreeProduct(product: string, source?) {
    let url = environment.paeApiUrl + `product/request-demo?product=${product}`;
    if (source) url += "&source=" + source;
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  public getOptionCardInstallationInstructions(product_id: number, card_product_id: number) {
    let url = `${environment.paeApiUrl}product/option-card-instructions/${product_id}/${card_product_id}`;
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  /**
   * Downloads an offline license file from paeapi. The file comes back base64-encoded from Lambda,
   * and API Gateway only decodes it when the request's Accept header matches a binary media type,
   * so this must ask for application/octet-stream (a plain <a href> sends the browser's text/html
   * Accept and saves the base64 text instead). The endpoint is public; no token is sent.
   * JWT software licenses need the activation segments; extensions pass only the license id.
   */
  public downloadLicenseFile(userId: string | number, licenseId: string | number, activation?: { computerKey: string; computerName: string; productKey: string }) {
    const segments = [userId, licenseId];
    if (activation) segments.push(activation.computerKey, activation.computerName, activation.productKey);
    const url = environment.paeApiUrl + "products/download_license/" + segments.map((s) => encodeURIComponent(String(s ?? ""))).join("/");
    return this.http.get(url, { headers: new HttpHeaders({ Accept: "application/octet-stream" }), responseType: "blob" });
  }

  public updateFirmware(product_id: number, firmware_version: number) {
    return this.http.post(environment.apiUrl + "products/update_firmware/", JSON.stringify({ productid: product_id, firmware_version: firmware_version }), this._helpers.getHttpOptions()).pipe();
  }

  public registerSoftware(productKey: string) {
    let args: any = {
      productkey: productKey,
    };
    args.type = "software";
    return this.http.post(environment.paeApiUrl + "product/register", JSON.stringify(args), this._helpers.getHttpOptions()).pipe();
  }

  public registerHardware(productKey: string, date: string, asset_id: any = undefined, refreshAllProducts = true) {
    let args: any = {
      purchase_date: date,
      serial_number: productKey,
    };
    if (asset_id) args.asset_id = asset_id;
    args.type = "hardware";
    return this.http.post(environment.paeApiUrl + "product/register", JSON.stringify(args), this._helpers.getHttpOptions()).pipe();
  }

  public registerFenderPromo(serial: string) {
    let args: any = {
      serial: serial,
    };
    return this.http.put(environment.paeApiUrl + "user/redeem-fender-promo", JSON.stringify(args), this._helpers.getHttpOptions()).pipe();
  }

  public deregisterHardware(assetId, refreshAllProducts = true) {
    return this.http.post(environment.paeApiUrl + `product/deregister/${assetId}`, null, this._helpers.getHttpOptions()).pipe(
      map((data) => {
        if (refreshAllProducts) {
          this.getProducts(true).subscribe((result) => {
            return data;
          });
        } else {
          return data;
        }
      })
    );
  }

  public getDealerProducts() {
    return this.http.get(environment.paeApiUrl + "dealers/products", this._helpers.getHttpOptions()).pipe();
  }

  public getDealerProductDetail(id) {
    return this.http.get(environment.apiUrl + "dealers/product/" + id, this._helpers.getHttpOptions()).pipe();
  }

  public getShopNewNoteworthy() {
    return this.http.get(environment.apiUrl + "users/shop_new_noteworthy", this._helpers.getHttpOptions()).pipe();
  }

  public getPreviousVersions(productId, licenseScheme) {
    return this.http.get(environment.paeApiUrl + `product/previous-versions?productId=${productId}&licenseScheme=${licenseScheme}`, this._helpers.getHttpOptions()).pipe();
  }

  private _handleMobileProductData(data) {
    let prods = {
      software: <any>[],
      extensions: <any>[],
      hardware: <any>[],
    };

    if (data) {
      this.productGroups = {};
      this.prodImagesMap = this.productImagesMap || {};
      this.productsByAcquired = [];
      this.productErpIDs = [];
      let erpIDsAdded = {};
      this.productsByStringIDs = {};
      for (var i in data) {
        if (data[i] && data[i].length) {
          data[i].forEach((item, index) => {
            if (item && item.product) {
              if (item.product.erpId && !erpIDsAdded[item.product.erpId]) {
                this.productErpIDs.push(item.product.erpId);
                this.productsByErpIDs[item.product.erpId] = item;
                erpIDsAdded[item.product.erpId] = true;
              }

              if (item.product.imageUrl) item.product.imageUrl = item.product.imageUrl.replace("studioone_content", "studioone-content");
              // Prefer fresh API image URLs and only use cache as fallback when API does not provide one.
              const cachedImage = this.prodImagesMap[item.product.id]?.url;
              if (item.product.imageUrl) {
                this.prodImagesMap[item.product.id] = {
                  fileName: "",
                  folder: "pictures",
                  id: item.product.id,
                  url: item.product.imageUrl,
                };
              } else if (cachedImage) {
                item.product.imageUrl = cachedImage.replace("studioone_content", "studioone-content");
              } else {
                this.prodImagesMap[item.product.id] = {
                  fileName: "",
                  folder: "pictures",
                  id: item.product.id,
                  url: item.product.imageUrl,
                };
              }
              this.productMap[item.id] = item;
              this.productsByStringIDs[item.product.stringId] = item;
              this.productsByAcquired.push(item);
              if (!this.productGroups[item.product.stringId]) {
                this.productGroups[item.product.stringId] = [];
                if (item.product.categoryId == "hardware") {
                  prods.hardware.push(item);
                } else if (item.product.categoryId == "software") {
                  prods.software.push(item);
                } else if (item.product.categoryId == "extension") {
                  prods.extensions.push(item);
                }
              }
            }
          });
        }
      }
      if (prods.software.length) this.prodArraySorter(prods.software);
      if (prods.software.length) this.prodArraySorter(prods.hardware);
      if (prods.software.length) this.prodArraySorter(prods.extensions);
      if (this.productsByAcquired.length) this.prodArraySorter(this.productsByAcquired);
    }

    return prods;
  }

  public prodArraySorter(arr, releaseDateOnly?) {
    arr.sort((a, b) => {
      let comparerA = releaseDateOnly ? a.releaseDate : a.releaseDate || a.aquired;
      let comparerB = releaseDateOnly ? b.releaseDate : b.releaseDate || b.aquired;

      // Format the date if not in the desired format 'YYYY-MM-DD'
      if (comparerA && comparerA.length != 10 && comparerA[4] == "-") {
        comparerA = comparerA.substring(0, 10);
      } else if (comparerA && comparerA.length != 10) {
        comparerA = moment(comparerA).format("YYYY-MM-DD");
      }
      if (comparerB && comparerB.length != 10 && comparerB[4] == "-") {
        comparerB = comparerB.substring(0, 10);
      } else if (comparerB && comparerB.length != 10) {
        comparerB = moment(comparerB).format("YYYY-MM-DD");
      }

      // If comparerA or comparerB is undefined or null, treat it as a very old date to sort it to the bottom
      let aDate = comparerA ? new Date(comparerA) : new Date(-8640000000000000);
      let bDate = comparerB ? new Date(comparerB) : new Date(-8640000000000000);

      // Perform the comparison
      if (aDate > bDate) {
        return -1; // a is newer
      } else if (aDate < bDate) {
        return 1; // b is newer
      } else {
        return 0; // a and b are the same age
      }
    });
  }

  private _handleProductData(data) {
    if (data) {
      this.sphereElligibleUpgradeProducts = [];
      this.productGroups = {};
      this.productsByAcquired = [];
      this.productsByReleaseDate = [];
      this.productErpIDs = [];
      let erpIDsAdded = {};
      this.productsByErpIDs = {};
      this.productsByStringIDs = {};
      this.productsByProductID = {};
      this.prodImagesMap = this.productImagesMap || {};
      try {
        data.forEach((item: ProductListItem, index) => {
          if (item) {
            if (item.imageUrl) item.imageUrl = item.imageUrl.replace("studioone_content", "studioone-content");
            // Prefer fresh API image URLs and only use cache as fallback when API does not provide one.
            const cachedImage = this.prodImagesMap[item.id]?.url;
            if (item.imageUrl) {
              this.prodImagesMap[item.id] = {
                fileName: "",
                folder: "pictures",
                id: item.id,
                url: item.imageUrl,
              };
            } else if (cachedImage) {
              item.imageUrl = cachedImage.replace("studioone_content", "studioone-content");
            } else {
              this.prodImagesMap[item.id] = {
                fileName: "",
                folder: "pictures",
                id: item.id,
                url: item.imageUrl,
              };
            }
            // item.prettyAquired = moment(item.aquired).format("ll");
            this.productMap[item.id] = item;
            this.productsByAcquired.push(item);
            this.productsByReleaseDate.push(item);

            if (item.stringId?.indexOf("studioapp") > -1) {
              let parts = item.stringId.split(".");
              let version = parts[parts.length - 1];
              if (version == "pro" || version == "artist" || version == "producer" || version == "beta") {
                this.hasElligibleS1ForExchange = true;
              } else if (version == "oem" && item.stringId.indexOf("free") == -1) {
                this.hasElligibleS1ForExchange = true;
              }
            }

            if (item.navId && !erpIDsAdded[item.navId]) {
              this.productErpIDs.push(item.navId);
              this.productsByErpIDs[item.navId] = item;
              erpIDsAdded[item.navId] = true;
            }
            this.productsByStringIDs[item.stringId] = item;
            this.productsByProductID[item.id] = item;
          }
        });

        this.productImagesMap = this.prodImagesMap;
        if (this.productsByAcquired.length) this.prodArraySorter(this.productsByAcquired);
        if (this.productsByReleaseDate.length) this.prodArraySorter(this.productsByReleaseDate, true);
      } catch (err) {
        this.products = false;
        this.productDataError$.next(true);
        return false;
      } finally {
        return data;
      }
    } else {
      return data;
    }
  }

  public clearCache() {
    // Without this the cache is empty but still claims to be loaded, and every caller that
    // guards a fetch on `hasGottenProducts` (the mobile Products and Home tabs, the
    // community product-ownership gate) quietly shows nothing. On the web a logout reloads
    // the page so the flag went with it; the app keeps running.
    this.hasGottenProducts = false;
    this.products$.next(false);
    this.learnProducts = false;
    this.products = false;
    this._savedProducts = false;
    this.productMap = {};
    this.productDetailsMap = {};
    this.productMap = {};
    this.productsByAcquired = [];
    this.productErpIDs = [];
    this.productsByErpIDs = {};
    this.productsByStringIDs = {};
    this.productsByProductID = {};
    this.prodImagesMap = {};
    this.productGroups = {};
  }

  getProductTransferDetails(productId: any) {
    const usePaeapi = this.userService.usePaeapiCheckout();
    const url = usePaeapi ? environment.paeApiUrl + `checkout/product/${productId}/prices` : environment.apiUrl + `stripe/retrieve_product/${productId}`;
    return this.http.get(url, this._helpers.getHttpOptions()).pipe(map((r: any) => (usePaeapi ? r : r?.data)));
  }

  registerFenderProduct(serial?: any, sku?: any, registerIfFound: boolean = true, purchaseDate?: string) {
    let url = environment.paeApiUrl + `product/find-fender-gear`;
    const params: string[] = [];

    if (serial) params.push(`serial=${serial}`);
    if (sku) params.push(`sku=${sku}`);
    if (registerIfFound) params.push(`registerIfFound=true`);
    if (purchaseDate) params.push(`purchaseDate=${purchaseDate}`);
    if (params.length > 0) url += `?${params.join("&")}`;

    return this.http.get(url, this._helpers.getHttpOptions()).pipe();
  }

  searchFenderProduct(serial?: any, sku?: any) {
    let url = environment.paeApiUrl + `product/fender-product-search`;
    const params: string[] = [];

    if (serial) params.push(`serial=${serial}`);
    if (sku) params.push(`sku=${sku}`);

    if (params.length > 0) url += `?${params.join("&")}`;

    return this.http.get(url, this._helpers.getHttpOptions(false)).pipe();
  }

  // Cache of the last downloads-page search so that when the user drills into a product and
  // hits Back, the previous query + results reappear instantly. Lives on the singleton service
  // rather than the component so component re-instantiation on route change doesn't clear it.
  public fenderGearSearchCache: { query: string; results: any[]; searched: boolean; mode?: "title" | "sku" | "serial" | "taxonomy"; nextToken?: string | null } | null = null;

  /**
   * Fetch the product snapshot and variant list for a Fender product family, keyed by productNo.
   * A productNo groups variants that differ by color/pickup/region — this endpoint returns the
   * representative snapshot plus all variants. Public — no token needed.
   *
   * Pass `clearSnapshot: true` (default on the downloads page) to force paeapi to rebuild the
   * snapshot from PIM instead of returning the cached DynamoDB copy.
   */
  getFenderGearByProductNo(productNo: string, opts: { clearSnapshot?: boolean } = {}) {
    let url = environment.paeApiUrl + `product/fender-gear/product-no/${encodeURIComponent(productNo)}`;
    if (opts.clearSnapshot) url += `?clearSnapshot=true`;
    return this.http.get(url, this._helpers.getHttpOptions(false)).pipe();
  }

  /**
   * The full list of exact-match taxonomy values the FMIC Products API's `taxonomy` search
   * param accepts (backend-sourced from SSM — see paeapi's `fenderProducts.getFmicTaxonomies()`),
   * for building a category/subcategory picker. Public — no token needed.
   */
  getFmicTaxonomies() {
    return this.http.get(environment.paeApiUrl + `product/fender-gear/taxonomies`, this._helpers.getHttpOptions(false)).pipe(map((result: any) => result?.taxonomies || []));
  }

  /**
   * Free-text search over the FMIC Products API for the public downloads page. No token —
   * hits `/product/fender-gear/search` on paeapi which is `authorize: false`.
   */
  searchFenderGear(opts: {
    title?: string;
    sku?: string;
    productNo?: string;
    bodyType?: string;
    series?: string;
    taxonomy?: string;
    pageSize?: number;
    nextToken?: string;
    withDownloadsOnly?: boolean;
    /**
     * Return guitars and basses one row per SKU (colour/finish) instead of one row per
     * productNo. Used by the missing-serial registration picker so a customer can identify the
     * exact instrument they own; the downloads page leaves it off and gets one row per family.
     */
    showInstrumentVariants?: boolean;
  }) {
    let url = environment.paeApiUrl + `product/fender-gear/search`;
    const params: string[] = [];
    if (opts.title) params.push(`title=${encodeURIComponent(opts.title)}`);
    if (opts.sku) params.push(`sku=${encodeURIComponent(opts.sku)}`);
    if (opts.productNo) params.push(`productNo=${encodeURIComponent(opts.productNo)}`);
    if (opts.bodyType) params.push(`bodyType=${encodeURIComponent(opts.bodyType)}`);
    if (opts.series) params.push(`series=${encodeURIComponent(opts.series)}`);
    if (opts.taxonomy) params.push(`taxonomy=${encodeURIComponent(opts.taxonomy)}`);
    if (opts.pageSize) params.push(`pageSize=${opts.pageSize}`);
    if (opts.nextToken) params.push(`nextToken=${encodeURIComponent(opts.nextToken)}`);
    if (opts.withDownloadsOnly) params.push(`withDownloadsOnly=true`);
    if (opts.showInstrumentVariants) params.push(`showInstrumentVariants=true`);
    if (params.length > 0) url += `?${params.join("&")}`;
    return this.http.get(url, this._helpers.getHttpOptions(false)).pipe();
  }

  /**
   * Resolve a batch of SKUs to lightweight list items (title, description, image, shop URL)
   * for cross-sell rails. No token — hits `/product/fender-gear/skus` on paeapi which is
   * `authorize: false`.
   *
   * The endpoint caps each request at 50 SKUs (to bound its upstream FMIC fan-out), so we split
   * larger lists into chunks of 50 and fire them in parallel, then merge the results. A chunk
   * that errors degrades to an empty list rather than failing the whole set.
   */
  getFenderGearBySkus(skus: string[]) {
    const list = (skus || []).filter(Boolean);
    if (!list.length) return of([] as FenderGearListItem[]);
    const CHUNK_SIZE = 50;
    const chunks: string[][] = [];
    for (let i = 0; i < list.length; i += CHUNK_SIZE) chunks.push(list.slice(i, i + CHUNK_SIZE));
    const requests = chunks.map((chunk) => {
      const url = environment.paeApiUrl + `product/fender-gear/skus?skus=${encodeURIComponent(chunk.join(","))}`;
      return this.http.get(url, this._helpers.getHttpOptions(false)).pipe(
        map((result: any) => (result?.items || []) as FenderGearListItem[]),
        catchError(() => of([] as FenderGearListItem[]))
      );
    });
    return forkJoin(requests).pipe(map((results) => results.reduce((acc, items) => acc.concat(items), [] as FenderGearListItem[])));
  }

  public elligleToRedeemStudioOneSeven: boolean = false;
  public s16ProductToUpgradeTo7: any;
  checkStudioOneSevenRedeemElligibility() {
    if (
      this.productMap[environment.product_ids.s16pro]?.assetIds &&
      (!this.productMap[environment.product_ids.s17pro] || !this.productMap[environment.product_ids.s17pro].assetIds?.length) &&
      environment.features.studio_one_seven &&
      new Date() < new Date("2025-01-01")
    ) {
      this.getDetailForAssetIds(environment.product_ids.s16pro, this.productMap[environment.product_ids.s16pro].assetIds).subscribe((result) => {
        let elligibleResults = result.filter((item) => new Date(item.aquired) >= this.s1SixToSevenElligiblePurchaseDate && !item.upgraded);
        if (elligibleResults.length) {
          this.elligleToRedeemStudioOneSeven = true;
          this.s16ProductToUpgradeTo7 = elligibleResults[0];
          this.redeemS17ElligibilityRetrieved$.next(true);
        } else {
          this.redeemS17ElligibilityRetrieved$.next(true);
        }
      });
    } else {
      this.redeemS17ElligibilityRetrieved$.next(true);
    }
  }
}
